'use client';

import * as React from 'react';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Bell as BellIcon } from '@phosphor-icons/react/dist/ssr/Bell';

import { usePopover } from '@src/hooks';
import {
  fetchNotifications,
  fetchUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '@src/api/actions/dashboard/notifications';

import { NotificationsPopover } from './Popovers';

export default function NotificationsButton({ iconColor }) {
  const popover = usePopover();
  const [notifications, setNotifications] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);

  const loadUnreadCount = React.useCallback(async () => {
    try {
      const response = await fetchUnreadNotificationCount();
      setUnreadCount(Number(response?.data?.unread_count || 0));
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const loadNotifications = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchNotifications({ page_size: 8 });
      const rows = Array.isArray(response?.data?.results)
        ? response.data.results
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setNotifications(rows);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  const handleOpen = React.useCallback(async () => {
    popover.handleOpen();
    await loadNotifications();
    await loadUnreadCount();
  }, [loadNotifications, loadUnreadCount, popover]);

  const handleMarkAllAsRead = React.useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true, read_at: item.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch {
      // Ignore soft failures in the navbar control.
    }
  }, []);

  const handleNotificationClick = React.useCallback(async (notification) => {
    if (!notification?.id || notification?.is_read) {
      popover.handleClose();
      return;
    }
    try {
      await markNotificationRead(notification.id);
      setNotifications((prev) => prev.map((item) => (
        item.id === notification.id
          ? { ...item, is_read: true, read_at: item.read_at || new Date().toISOString() }
          : item
      )));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Ignore soft failures in the navbar control.
    } finally {
      popover.handleClose();
    }
  }, [popover]);

  return (
    <React.Fragment>
      <Tooltip title="Notifications">
        <Badge
          badgeContent={unreadCount > 99 ? '99+' : unreadCount}
          color="error"
          invisible={!unreadCount}
        >
          <IconButton onClick={handleOpen} ref={popover.anchorRef}>
            <BellIcon color={iconColor} />
          </IconButton>
        </Badge>
      </Tooltip>
      <NotificationsPopover
        anchorEl={popover.anchorRef.current}
        loading={loading}
        notifications={notifications}
        onClose={popover.handleClose}
        onMarkAllAsRead={handleMarkAllAsRead}
        onNotificationClick={handleNotificationClick}
        open={popover.open}
      />
    </React.Fragment>
  );
}
